import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, getToken, API } from "@/lib/api";
import { Trash2, Download } from "lucide-react";

const SOURCE_LABELS = {
  "four-levels": "Four Levels",
};

export default function AdminEnrollments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/enrollments");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (e) => {
    if (!window.confirm(`Remove enrollment for ${e.email}?`)) return;
    try {
      await api.delete(`/admin/enrollments/${e.id}`);
      toast.success("Removed.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  const downloadCsv = () => {
    const token = getToken();
    const link = document.createElement("a");
    link.href = `${API}/admin/enrollments.csv`;
    link.rel = "noopener";
    // FastAPI accepts token via header; fallback to fetch+blob for auth
    fetch(link.href, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "enrollments.csv";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed."));
  };

  const totalToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return items.filter((i) => (i.created_at || "").startsWith(today)).length;
  }, [items]);

  return (
    <div data-testid="admin-enrollments">
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-10">
        <div>
          <div className="eyebrow mb-2">Sales</div>
          <h1 className="display text-[36px]">Enrollments</h1>
          <p className="text-[14px] text-brand-ink/60 mt-2">
            Everyone who clicked "Begin the Four Levels Experience" and was sent to Stripe. Cross-reference with your Stripe dashboard to see who completed payment.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-brand-ink/50">Today</div>
            <div className="font-serif text-[24px] text-brand-ink tabular-nums" data-testid="enrollments-today-count">{totalToday}</div>
          </div>
          <button
            onClick={downloadCsv}
            data-testid="enrollments-download-csv"
            className="inline-flex items-center gap-2 rounded-full border border-brand-ink/30 bg-white px-5 py-2.5 text-brand-ink text-[13.5px] hover:border-brand-blue hover:text-brand-blue transition-colors"
          >
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-brand-ink/50 text-[14px]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-brand-rule bg-brand-cream/40 p-10 text-center">
          <p className="text-brand-ink/70">No enrollments yet.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-brand-rule/70 overflow-hidden bg-brand-cream">
          <table className="w-full text-[14px]">
            <thead className="text-left text-brand-ink/60 border-b border-brand-rule/70">
              <tr>
                <th className="px-5 py-3 font-medium">When</th>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">From</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-brand-rule/40 last:border-0" data-testid={`enrollment-row-${e.id}`}>
                  <td className="px-5 py-4 text-brand-ink/70 tabular-nums text-[13px]">
                    {new Date(e.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="px-5 py-4 text-brand-ink font-medium">{e.name}</td>
                  <td className="px-5 py-4">
                    <a href={`mailto:${e.email}`} className="text-brand-blue/90 hover:text-brand-blue">{e.email}</a>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-block px-2.5 py-1 rounded-full text-[11px] bg-brand-purple/10 text-brand-purple">
                      {SOURCE_LABELS[e.source] || e.source}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    <button onClick={() => remove(e)} data-testid={`enrollment-delete-${e.id}`} className="inline-flex items-center gap-1.5 text-brand-ink/70 hover:text-red-600">
                      <Trash2 size={13} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
